/**
 * WebSocket envelope format — encoder, decoder, types.
 *
 * Format specification: docs/4-engineering/architecture/ws-envelope.md
 *
 * Every frame is a WebSocket binary frame containing a fixed 39-byte header
 * followed by optional JSON and binary sections:
 *
 *   [version:1][kind:1][flags:1][scope_id:16][corr_id:16][json_len:4 LE]
 *   [json_section: json_len bytes][binary_section: remainder]
 */

import {
  PROTOCOL_VERSION,
  FLAG_HAS_SCOPE_ID,
  FLAG_HAS_CORR_ID,
  FLAG_HAS_JSON,
  FLAG_HAS_BINARY,
} from './envelope-kinds';

export const HEADER_SIZE = 39;
export const NIL_UUID_BYTES = new Uint8Array(16);

export interface Envelope {
  version: number;
  kind: number;
  flags: number;
  scopeId: Uint8Array; // 16 bytes
  corrId: Uint8Array;  // 16 bytes
  json: Uint8Array;
  binary: Uint8Array;
}

export interface EncodeArgs {
  kind: number;
  scopeId?: Uint8Array | string;
  corrId?: Uint8Array | string;
  json?: Uint8Array;
  binary?: Uint8Array;
}

export type EnvelopeErrorCode =
  | 'truncated'
  | 'unsupported_version'
  | 'inconsistent_json_flag'
  | 'json_len_exceeds_frame'
  | 'inconsistent_binary_flag';

export class EnvelopeError extends Error {
  constructor(public code: EnvelopeErrorCode, message: string) {
    super(message);
    this.name = 'EnvelopeError';
  }
}

/** Convert a hyphenated UUID string to 16 binary bytes. */
export function uuidToBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, '');
  if (hex.length !== 32) {
    throw new Error(`invalid UUID: ${uuid}`);
  }
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    bytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

/** Convert 16 binary bytes to a hyphenated UUID string. */
export function bytesToUuid(bytes: Uint8Array): string {
  if (bytes.length !== 16) {
    throw new Error(`expected 16 bytes, got ${bytes.length}`);
  }
  const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.substring(0, 8)}-${hex.substring(8, 12)}-${hex.substring(12, 16)}-${hex.substring(16, 20)}-${hex.substring(20, 32)}`;
}

function toUuidBytes(input: Uint8Array | string | undefined): Uint8Array {
  if (input == null) return NIL_UUID_BYTES;
  if (typeof input === 'string') return uuidToBytes(input);
  if (input.length !== 16) throw new Error(`expected 16 UUID bytes, got ${input.length}`);
  return input;
}

/** Parse an envelope from raw bytes. Performs P1-P7 structural validation. */
export function parseEnvelope(bytes: Uint8Array): Envelope {
  // P1: minimum header length
  if (bytes.length < HEADER_SIZE) {
    throw new EnvelopeError(
      'truncated',
      `envelope truncated: got ${bytes.length} bytes, expected at least ${HEADER_SIZE}`,
    );
  }

  // P2: protocol version
  const version = bytes[0];
  if (version !== PROTOCOL_VERSION) {
    throw new EnvelopeError(
      'unsupported_version',
      `unsupported envelope version: got ${version}, expected ${PROTOCOL_VERSION}`,
    );
  }

  const kind = bytes[1];
  const flags = bytes[2];

  const scopeId = bytes.slice(3, 19);
  const corrId = bytes.slice(19, 35);

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const jsonLen = view.getUint32(35, true); // little-endian

  // P5: HAS_JSON flag vs json_len
  const hasJsonFlag = (flags & FLAG_HAS_JSON) !== 0;
  if (hasJsonFlag !== jsonLen > 0) {
    throw new EnvelopeError(
      'inconsistent_json_flag',
      `inconsistent HAS_JSON flag (${hasJsonFlag}) vs json_len (${jsonLen})`,
    );
  }

  // P6: frame length covers declared json
  const jsonEnd = HEADER_SIZE + jsonLen;
  if (bytes.length < jsonEnd) {
    throw new EnvelopeError(
      'json_len_exceeds_frame',
      `json_len (${jsonLen}) exceeds available frame bytes (${bytes.length - HEADER_SIZE})`,
    );
  }

  const json = bytes.slice(HEADER_SIZE, jsonEnd);
  const binary = bytes.slice(jsonEnd);

  // P7: HAS_BINARY flag vs derived binary length
  const hasBinaryFlag = (flags & FLAG_HAS_BINARY) !== 0;
  if (hasBinaryFlag !== binary.length > 0) {
    throw new EnvelopeError(
      'inconsistent_binary_flag',
      `inconsistent HAS_BINARY flag (${hasBinaryFlag}) vs binary_len (${binary.length})`,
    );
  }

  return { version, kind, flags, scopeId, corrId, json, binary };
}

/**
 * Encode an envelope to raw bytes suitable for WebSocket.send().
 *
 * The flags byte is derived from which fields are provided (not from their
 * values). Passing `scopeId: undefined` clears HAS_SCOPE_ID and zero-pads the
 * slot. Passing any UUID (including the nil UUID) sets the flag — the receiver
 * treats it as a meaningful UUID. Same rule for `corrId`, `json`, and `binary`.
 */
export function encodeEnvelope(args: EncodeArgs): Uint8Array {
  let flags = 0;
  if (args.scopeId != null) flags |= FLAG_HAS_SCOPE_ID;
  if (args.corrId != null) flags |= FLAG_HAS_CORR_ID;
  if (args.json != null) flags |= FLAG_HAS_JSON;
  if (args.binary != null) flags |= FLAG_HAS_BINARY;

  const jsonBytes = args.json ?? new Uint8Array(0);
  const binaryBytes = args.binary ?? new Uint8Array(0);
  const jsonLen = jsonBytes.length;

  const total = HEADER_SIZE + jsonLen + binaryBytes.length;
  const buf = new Uint8Array(total);
  const view = new DataView(buf.buffer);

  buf[0] = PROTOCOL_VERSION;
  buf[1] = args.kind;
  buf[2] = flags;
  buf.set(toUuidBytes(args.scopeId), 3);
  buf.set(toUuidBytes(args.corrId), 19);
  view.setUint32(35, jsonLen, true); // little-endian
  buf.set(jsonBytes, HEADER_SIZE);
  buf.set(binaryBytes, HEADER_SIZE + jsonLen);

  return buf;
}

/** Helper: build a JSON section from a plain object via UTF-8 encoding. */
export function encodeJson(obj: unknown): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(obj));
}

/** Helper: parse the JSON section as a plain object. */
export function decodeJson<T = unknown>(bytes: Uint8Array): T {
  return JSON.parse(new TextDecoder().decode(bytes)) as T;
}
